import { SURGERY_BASE_URL } from "@qendo/medical";
import { MedicalType } from "@qendo/medical/types";
import { OptionType } from "@qendo/types";
import type { DateArg } from "date-fns";
import { Stack } from "expo-router";
import { useMemo, useState } from "react";

import { Button } from "~/components/Button";
import { FilteredList } from "~/components/layout/FilteredList";
import { ListingItem } from "~/components/layout/ListingItem";
import { Page } from "~/components/layout/Page";
import { SectionHeader } from "~/components/layout/SectionHeader";
import { Authorize } from "~/components/permission/Authorize";
import { Can } from "~/components/permission/Can";
import { Only } from "~/components/permission/Only";
import { ShareButton } from "~/components/ShareButton";
import type { Surgery } from "~/entities/medical/surgery";
import { useMedical } from "~/providers/MedicalProvider";
import { useOptions } from "~/providers/OptionsProvider";
import { formatShortDateForHumans } from "~/util/dates";

interface SurgeryListingItem {
    id: string;
    title: string;
    procedure_date: DateArg<Date> | null;
}

const type = MedicalType.surgery;

export default function SurgeriesIndex() {
    const { listMedical, refreshMedicalCache } = useMedical();
    const { findOption } = useOptions();
    const [refreshing, setRefreshing] = useState(false);

    const refresh = async () => {
        if (refreshing) {
            return;
        }

        setRefreshing(true);
        await refreshMedicalCache(type).finally(() => setRefreshing(false));
    };

    const items: SurgeryListingItem[] = useMemo(() => {
        return listMedical(type).map((s: Surgery) => ({
            id: s.id as string,
            title: findOption(OptionType.surgeries, s.option_id)?.title as string,
            procedure_date: s.procedure_date,
        }));
    }, [findOption, listMedical]);

    const has_items = items.length > 0;

    return (
        <Authorize ability="view" section="surgeries">
            <Stack.Screen
                options={{
                    headerRight: () => <ShareButton title="Share surgeries" initial={["surgeries", "photos"]} />,
                }}
            />
            <Page spacer refreshWith={refresh} isRefreshing={refreshing} scrollable={!has_items}>
                <SectionHeader
                    title="Surgeries and interventions"
                    icon="ambulance"
                    button={() => (
                        <Can ability="update" section="surgeries">
                            <Button
                                title="Add procedure"
                                variant="primary"
                                size="lg"
                                href="/health-profile/surgeries/create"
                            />
                        </Can>
                    )}
                >
                    Document your surgical history and medical interventions to keep them organised and accessible.
                </SectionHeader>
                <Only when={items.length}>
                    <FilteredList<SurgeryListingItem>
                        data={items}
                        searchWith={(item, term) => item.title.toLowerCase().includes(term.toLowerCase())}
                        searchPlaceholder="Search by name"
                        refreshWith={refresh}
                        isRefreshing={refreshing}
                        renderItem={({ item }) => (
                            <ListingItem
                                title={item.title}
                                subtitle={item.procedure_date ? formatShortDateForHumans(item.procedure_date) : null}
                                hideChevron
                                href={{ pathname: `${SURGERY_BASE_URL}/[id]/show`, params: { id: item.id } }}
                                last
                            />
                        )}
                    />
                </Only>
            </Page>
        </Authorize>
    );
}
